import * as React from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "Components/ui";
import { RefreshCw, SearchX } from "Components/ui/icons";
import { getApiErrorMessage, getApiErrorStatus } from "api/apiError";
import { formatINR } from "utils/format";
import { BillingStatusChip, ENFORCEMENT_LABELS, ModeChip } from "../Components/StatusChips";
import TeleopdLinkBadge from "../Components/TeleopdLinkBadge";
import { useNow } from "../hooks";
import useTenantBillingData from "./data";
import {
  InvoiceEditorState,
  PaymentDialogState,
  ScheduleEditorState,
  TenantBillingTab,
} from "./types";
import ChargesTab from "./Components/ChargesTab";
import ConnectionTab from "./Components/ConnectionTab";
import InvoiceDetailModal from "./Components/InvoiceDetailModal";
import InvoiceEditorModal from "./Components/InvoiceEditorModal";
import InvoicesTab from "./Components/InvoicesTab";
import OverviewTab from "./Components/OverviewTab";
import PaymentsTab from "./Components/PaymentsTab";
import RecordPaymentModal from "./Components/RecordPaymentModal";
import RulesTab from "./Components/RulesTab";
import ScheduleEditorModal from "./Components/ScheduleEditorModal";
import { reminderRecipients } from "./rules";

const TABS: Array<{ id: TenantBillingTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "charges", label: "Charges" },
  { id: "invoices", label: "Invoices" },
  { id: "payments", label: "Payments" },
  { id: "rules", label: "Rules" },
  { id: "connection", label: "Connection" },
];

const parseTab = (search: string): TenantBillingTab => {
  const value = new URLSearchParams(search).get("tab");
  const match = TABS.find((tab) => tab.id === value);
  return match ? match.id : "overview";
};

const BILLING_CRUMB = { label: "Billing", to: "/billing" };

/** /billing/:tenantId — one client's billing: overview, charges, invoices, payments, rules, link. */
const TenantBillingPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const history = useHistory();
  const location = useLocation();
  useNow();
  const { billingQuery, linkQuery, permissions, locked, unconnected } =
    useTenantBillingData(tenantId);
  const tabs = unconnected ? TABS.filter((entry) => entry.id === "connection") : TABS;
  const tab = unconnected ? "connection" : parseTab(location.search);
  // Locked and not connected: nothing to do here; the next step is on the Tenants page.
  // Known not to have synced yet (not just a link status that failed to load).
  const neverSynced = unconnected && !linkQuery.isError;
  const nothingToDo = locked && neverSynced;

  const [invoiceId, setInvoiceId] = React.useState<string | null>(null);
  const [paymentDialog, setPaymentDialog] = React.useState<PaymentDialogState>(undefined);
  const [invoiceEditor, setInvoiceEditor] = React.useState<InvoiceEditorState | null>(null);
  const [scheduleEditor, setScheduleEditor] = React.useState<ScheduleEditorState | null>(null);

  const goToTab = React.useCallback(
    (next: TenantBillingTab) => {
      history.replace({
        pathname: location.pathname,
        search: next === "overview" ? "" : `?tab=${next}`,
      });
    },
    [history, location.pathname]
  );

  const billing = billingQuery.data;
  const link = linkQuery.data;

  if (billingQuery.isLoading || linkQuery.isLoading) {
    return (
      <Page aria-busy="true">
        <PageHeader breadcrumbs={[BILLING_CRUMB, { label: "Loading…" }]} title="Billing" />
        <Skeleton className="h-10 w-full max-w-xl" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </Page>
    );
  }

  if (billingQuery.isError || !billing) {
    const notFound = getApiErrorStatus(billingQuery.error) === 404;
    return (
      <Page>
        <PageHeader
          breadcrumbs={[BILLING_CRUMB, { label: notFound ? "Not found" : "Error" }]}
          title="Billing"
        />
        <Card>
          {notFound ? (
            <EmptyState
              icon={<SearchX />}
              title="Client not found"
              description="It may have been deleted. Pick a client from the billing overview."
              action={
                <Button asChild size="sm" variant="secondary">
                  <Link to="/billing">Open the billing overview</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState
              title="Billing didn't load"
              description={getApiErrorMessage(billingQuery.error)}
              action={
                <Button
                  variant="secondary"
                  leftIcon={<RefreshCw />}
                  onClick={() => billingQuery.refetch()}
                >
                  Try again
                </Button>
              }
            />
          )}
        </Card>
      </Page>
    );
  }

  const { state, account } = billing;
  const warningDays = account.policy.warningDays;
  const openInvoice = (id: string) => setInvoiceId(id);
  const openPayment = (id: string | null) => setPaymentDialog(id);
  const refreshing = (billingQuery.isFetching || linkQuery.isFetching) && !billingQuery.isLoading;

  return (
    <Page>
      <PageHeader
        breadcrumbs={[BILLING_CRUMB, { label: billing.tenant.name }]}
        title={billing.tenant.name}
        meta={
          <>
            {billing.tenant.slug && (
              <Badge size="md" className="font-mono" title="pm2 app">
                {billing.tenant.slug}
              </Badge>
            )}
            <StatusBadge domain="stage" status={billing.tenant.stage} size="md" />
            {!unconnected && <BillingStatusChip status={state.status} size="md" />}
          </>
        }
        description={
          link && link.tenantUri ? (
            <span className="block truncate font-mono">{link.tenantUri}</span>
          ) : undefined
        }
        actions={
          <Button
            variant="secondary"
            leftIcon={<RefreshCw />}
            loading={refreshing}
            onClick={() => {
              billingQuery.refetch();
              linkQuery.refetch();
            }}
          >
            Refresh
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          {!unconnected && (
            <>
              <span>
                Outstanding{" "}
                <span className="font-semibold tabular-nums text-foreground">
                  {formatINR(state.outstanding)}
                </span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                TeleOpd <ModeChip mode={state.mode} />
              </span>
              <span>{ENFORCEMENT_LABELS[state.enforcement]}</span>
            </>
          )}
          {link && (
            <TeleopdLinkBadge
              linked={link.linked}
              enabled={link.enabled}
              lastSyncAt={link.lastSyncAt}
              stale={link.stale}
            />
          )}
        </div>
      </PageHeader>

      {nothingToDo && (
        <Alert
          tone="neutral"
          title="Locked, and its TeleOpd isn't connected"
          action={
            <Button asChild size="sm" variant="secondary">
              <Link to="/clients">Open Tenants</Link>
            </Button>
          }
        >
          SAP is in testing, so this client is read-only here. Make it actionable on the Tenants
          page first; then its TeleOpd can be connected here.
        </Alert>
      )}
      {locked && !unconnected && (
        <Alert tone="neutral" title="Locked until production release">
          SAP is in testing, so this client is read-only here: you can view its billing, but
          charges, invoices, payments, rules and the TeleOpd connection can't change. Move it to
          Actionable on the Tenants page to work on it.
        </Alert>
      )}
      {!locked && neverSynced && (
        <Alert tone="info" title="TeleOpd isn't connected yet">
          Billing opens once this client's TeleOpd has synced with SAP. Issue its keys below, set
          them on the TeleOpd server and wait for the first sync.
        </Alert>
      )}

      {!nothingToDo && (
        <Tabs value={tab} onValueChange={(value) => goToTab(value as TenantBillingTab)}>
          <TabsList aria-label="Billing sections" className="overflow-x-auto">
            {tabs.map((entry) => (
              <TabsTrigger key={entry.id} value={entry.id}>
                {entry.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={tab} className="mt-6">
            {tab === "overview" && (
              <OverviewTab
                tenantId={tenantId}
                billing={billing}
                link={link}
                permissions={permissions}
                onOpenInvoice={openInvoice}
                onRecordPayment={openPayment}
                onGoToTab={goToTab}
              />
            )}
            {tab === "charges" && (
              <ChargesTab
                tenantId={tenantId}
                billing={billing}
                permissions={permissions}
                onAddSchedule={() => setScheduleEditor({ mode: "create" })}
                onEditSchedule={(schedule) => setScheduleEditor({ mode: "edit", schedule })}
                onAddInvoice={() => setInvoiceEditor({ mode: "create" })}
                onOpenInvoice={openInvoice}
              />
            )}
            {tab === "invoices" && (
              <InvoicesTab
                tenantId={tenantId}
                permissions={permissions}
                onOpenInvoice={openInvoice}
                onAddInvoice={() => setInvoiceEditor({ mode: "create" })}
              />
            )}
            {tab === "payments" && (
              <PaymentsTab
                tenantId={tenantId}
                permissions={permissions}
                onOpenInvoice={openInvoice}
                onRecordPayment={() => openPayment(null)}
              />
            )}
            {tab === "rules" && (
              <RulesTab
                tenantId={tenantId}
                billing={billing}
                link={link}
                canWrite={permissions.canWrite}
                canApprove={permissions.canApprove}
              />
            )}
            {tab === "connection" && (
              <ConnectionTab
                tenantId={tenantId}
                link={link}
                loading={linkQuery.isLoading}
                error={linkQuery.error}
                onRetry={() => {
                  linkQuery.refetch();
                }}
                permissions={permissions}
              />
            )}
          </TabsContent>
        </Tabs>
      )}

      {invoiceId && (
        <InvoiceDetailModal
          tenantId={tenantId}
          invoiceId={invoiceId}
          permissions={permissions}
          reminderRecipients={reminderRecipients(account.contacts, billing.notifications)}
          onClose={() => setInvoiceId(null)}
          onRecordPayment={(id) => openPayment(id)}
          onEdit={(invoice) => setInvoiceEditor({ mode: "edit", invoice })}
        />
      )}
      {invoiceEditor && (
        <InvoiceEditorModal
          tenantId={tenantId}
          state={invoiceEditor}
          warningDays={warningDays}
          onClose={() => setInvoiceEditor(null)}
        />
      )}
      {scheduleEditor && (
        <ScheduleEditorModal
          tenantId={tenantId}
          state={scheduleEditor}
          warningDays={warningDays}
          onClose={() => setScheduleEditor(null)}
        />
      )}
      {paymentDialog !== undefined && (
        <RecordPaymentModal
          tenantId={tenantId}
          defaultInvoiceId={paymentDialog}
          onClose={() => setPaymentDialog(undefined)}
        />
      )}
    </Page>
  );
};

export default TenantBillingPage;
